import { getUrlFromAxiosError } from '@/modules/app-bridge/utils/GetUrlFromAxiosError';
import type { AxiosInstance, AxiosError } from 'axios';
import type { UserStore } from '@/stores/userStore';

const checkTokenValidity = async (tokenNew: any, tokenOld: any) => {
  const { default: equal } = await import('fast-deep-equal');
  return tokenNew && !equal(tokenNew, tokenOld);
};

/**
 * Higher order function which returns a closure with access to certain nuxt helpers.
 * @param $log Logger for obvious reasons.
 * @param instance Axios instance required to retry the request.
 * @param userStore Store instance required to read and write user token.
 */
export const unauthorizedResponseInterceptor =
  ($log: ReturnType<typeof logger.createLogMethods>, instance: AxiosInstance, userStore: UserStore) =>
  async (error: AxiosError) => {
    if (error?.response?.status === 401) {
      $log.debug(`401 response for ${getUrlFromAxiosError(error)}`);

      if (userStore.getIsAuthenticated) {
        const tokenOld = deepClone(toRaw(userStore.token));

        // Fetch new token...
        await userStore.refreshToken();

        // ...compare with the old token...
        const isTokenValidAndChanged = await checkTokenValidity(userStore.token, tokenOld);

        // ...then try to repeat the request
        if (!isTokenValidAndChanged) {
          $log.debug(`Invalid token value`);
        } else if (!error.config) {
          $log.debug(`Missing request config object`);
        } else {
          $log.debug(`Retrying request for ${getUrlFromAxiosError(error)}`);
          return instance.request(error.config);
        }

        $log.debug(`Skipping retrying request and re-throwing original error for ${getUrlFromAxiosError(error)}`);
      }
    }

    return Promise.reject(error);
  };
